import {toast} from "sonner";

import {captureGpsFailure} from "@/src/modules/core/lib/glitchtip/device-capture";
import {formatGpsCoordinates} from "@/src/modules/core/lib/format-gps-coordinates";
import {isGeolocationPermissionDenied} from "@/src/modules/core/lib/geolocation-service";
import {isNativeMobileGeolocationPlatform} from "@/src/modules/core/lib/geolocation-platform";
import {resolveCoordinatesForSubmit} from "@/src/modules/core/lib/resolve-coordinates-for-submit";
import type {ResolveCoordinatesOptions, ResolvedGpsPayload} from "@/src/modules/core/types/geolocation";

const LOCATION_CAPTURE_FAILURE =
  "Couldn't capture your location. Turn on GPS / location and make sure you have signal, then try again.";

// Desktop reaches the no-fix path only when the property has no saved coordinates
// (it falls back to property coords whenever they exist). Turning on GPS won't help.
const PROPERTY_LOCATION_MISSING =
  "This property has no saved location. Ask your admin to add it before continuing.";

export type ResolveGpsStringOptions = {
  onFailure?: "silent" | "toast";
  captureContext?: {
    feature: string;
    operation: string;
  };
};

export async function resolveGpsForSubmit(
  options: ResolveCoordinatesOptions,
  config: ResolveGpsStringOptions = {onFailure: "toast"},
): Promise<ResolvedGpsPayload | null> {
  // Mobile must capture a live device fix, so deny blocks immediately. Desktop
  // falls through to the property-coordinate fallback inside the resolver below.
  if (isNativeMobileGeolocationPlatform() && isGeolocationPermissionDenied()) {
    if (config.captureContext) {
      captureGpsFailure(config.captureContext);
    }
    return null;
  }

  const fix = await resolveCoordinatesForSubmit(options);
  const formatted = formatGpsCoordinates(fix);

  if (formatted && fix) {
    if (isNativeMobileGeolocationPlatform() && fix.source !== "device") {
      if (config.onFailure === "toast") {
        toast.error(LOCATION_CAPTURE_FAILURE);
      }
      if (config.captureContext) {
        captureGpsFailure(config.captureContext);
      }
      return null;
    }

    if (config.captureContext && fix.source !== "device") {
      // GPS fallback success is expected — failures use captureGpsFailure above.
    }

    return {
      gps_coordinates: formatted,
      gps_source: fix.source,
    };
  }

  if (config.onFailure === "toast") {
    toast.error(
      isNativeMobileGeolocationPlatform()
        ? LOCATION_CAPTURE_FAILURE
        : PROPERTY_LOCATION_MISSING,
    );
  }

  if (config.captureContext) {
    captureGpsFailure(config.captureContext);
  }

  return null;
}

/** @deprecated Prefer `resolveGpsForSubmit` when `gps_source` is needed. */
export async function resolveGpsCoordinatesString(
  options: ResolveCoordinatesOptions,
  config: ResolveGpsStringOptions = {onFailure: "toast"},
): Promise<string | null> {
  const resolved = await resolveGpsForSubmit(options, config);
  return resolved?.gps_coordinates ?? null;
}
